'use client'

import { useCallback, useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import {
  birdiePastRoundsCopy,
  birdieRoundCardCopy
} from 'src/components/BirdieRoundCard/birdieCopy'
import { shortenHash } from 'src/components/CrashMultiplierHistory/crashRoundFairness'
import './birdie-board.scss'
import type { BirdieRoundFairness } from './pastRounds'
import type { Translate } from './tileViewModel'

/**
 * The Birdie provably-fair BODY (B33) — one rendering, three entry points.
 *
 * Until B33 this block was drawn inside `BirdieRoundSheet` and nowhere else, so
 * a Birdie round opened from the bet history went to the shared
 * `<CrashRoundModal>` as a CRASH round (the reveal's `game === 'birdie'` fell
 * into the discriminator's default). Now the disc rail's sheet, the history
 * sidebar's sheet and the shared modal all mount THIS, and the three cannot
 * drift: the commitment, the reveal, Birdie's own card-and-catalogue terms, and
 * the hand-off to the public verifier.
 *
 * CONTENT CLASSES ONLY. The sheet's chassis is `.birdie-sheet__*` and the
 * shared modal's is `.crash-round-modal__*`; everything here is `.birdie-rc__*`
 * — the round card's own vocabulary, which is where the block sits in the sheet
 * — so it renders inside either chassis without dragging a second one in.
 *
 * NO RECOMPUTE, NO VERDICT. The public verifier's Birdie mappers land in M6
 * (`pastRounds.verifierLink`); the widget has no Birdie derivation and must not
 * invent one, so nothing here may read as "verified". The pill hands the round
 * to the tool with every value it will need pre-filled, and says only that.
 *
 * `t` is the HOST's translator: the sheet's language-pinned one, the modal's
 * `useClientSideTranslation` one. Both are the board's loose `Translate` shape;
 * the copy factories want i18next's branded `TFunction` for the same function.
 */
type Props = Readonly<{
  fairness: BirdieRoundFairness | undefined
  /** `verifierLink(round)` — the shared URL contract, built by the host. */
  verifierHref: string
  t: Translate
  /**
   * Start with the verifier-only rows unfolded. Defaults to false: a player
   * opening the sheet sees the five rows that read as a proof, and the three
   * that only the tool consumes wait behind one disclosure.
   */
  detailsOpen?: boolean
}>

const RESET_COPIED_MS = 2000

/**
 * The widest value shown whole. `shortenHash`'s head-and-tail form is itself 13
 * characters, so eliding anything that short hides digits for no width — and
 * Birdie's card draw is exactly 13 hex. The sheet showed it whole before B33
 * (its own formatter kept values up to 14) and still does; only the long
 * hashes take the shared shape. The clipboard always gets the full value.
 */
const SHOW_WHOLE_MAX_CHARS = 14

const displayValue = (value: string): string =>
  value.length <= SHOW_WHOLE_MAX_CHARS ? value : shortenHash(value)

/**
 * Clipboard write with the `execCommand` fallback for the embedded / iframe
 * contexts where the async Clipboard API is blocked. Copied from
 * `CrashRoundModal.tsx` rather than imported — that module is the crash sheet's,
 * and Birdie does not depend on crash surfaces (the one exception is
 * `shortenHash`, a pure formatter the bet-history rows already share).
 */
async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value)
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = value
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    try {
      document.execCommand('copy')
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

/** The eight row labels, resolved by the host's `t`. */
export interface BirdieFairnessLabels {
  serverSeedHash: string
  revealedSeed: string
  chainRoot: string
  chainIndex: string
  cardDraw: string
  paytableHash: string
  catalogue: string
  clip: string
}

export interface BirdieFairnessRow {
  key: string
  label: string
  /** The FULL value — what the clipboard gets. Shortened only for display. */
  value: string
}

/**
 * The reveal as labelled rows, in reading order: the commitment, the seed, the
 * chain link, then Birdie's own terms. Whatever the reveal did not carry is
 * simply not a row — a round settled by an older engine renders the part it
 * has rather than a block of dashes. Pure, so a test can pin the order.
 */
export function birdieFairnessRows(
  fairness: BirdieRoundFairness | undefined,
  labels: BirdieFairnessLabels
): BirdieFairnessRow[] {
  const rows: BirdieFairnessRow[] = []
  if (fairness === undefined) return rows
  const push = (key: string, label: string, value: string | number | undefined): void => {
    if (value === undefined || value === '') return
    rows.push({ key, label, value: String(value) })
  }
  push('commitment', labels.serverSeedHash, fairness.serverSeedHash)
  push('seed', labels.revealedSeed, fairness.serverSeed)
  push('chainRoot', labels.chainRoot, fairness.chainRootHash)
  push('chainIndex', labels.chainIndex, fairness.chainIndex)
  // The CARD's own domain-separated draw, which is what picked the golfer and
  // the green. The mockup's "client seed" and "nonce" rows have no counterpart
  // in the Birdie reveal — the chain root and the chain index are those two,
  // under the names the rest of the platform's fairness surfaces already use.
  push('cardDraw', labels.cardDraw, fairness.cardDraw)
  push('paytable', labels.paytableHash, fairness.paytableHash)
  push(
    'catalogue',
    labels.catalogue,
    fairness.catalogueHash === undefined
      ? fairness.catalogueVersion
      : `v${fairness.catalogueVersion ?? '?'} ${fairness.catalogueHash}`
  )
  push('clip', labels.clip, fairness.clipSha256 ?? fairness.clipId)
  return rows
}

/**
 * The rows only the verifier consumes. A player reads the commitment, the
 * revealed seed, the chain link and the clip as a proof; the card draw is
 * derivable from the seed, and the paytable and catalogue hashes are the
 * commitments the TOOL checks the prices and the card pool against. They stay
 * in the reveal (and in the verifier URL) untouched — the sheet just folds them
 * behind one disclosure so the block reads like the other games' sheets.
 */
export const FAIRNESS_DETAIL_KEYS: ReadonlySet<string> = new Set([
  'cardDraw',
  'paytable',
  'catalogue'
])

export function splitFairnessRows(rows: readonly BirdieFairnessRow[]): {
  primary: BirdieFairnessRow[]
  details: BirdieFairnessRow[]
} {
  const primary: BirdieFairnessRow[] = []
  const details: BirdieFairnessRow[] = []
  for (const row of rows) {
    ;(FAIRNESS_DETAIL_KEYS.has(row.key) ? details : primary).push(row)
  }
  return { primary, details }
}

/**
 * One provably-fair value: its label, the value in monospace, and the whole row
 * as a copy button. The FULL value goes to the clipboard even though a shortened
 * one is displayed — a player verifying a round needs the real hash.
 */
function FairValue({
  label,
  value,
  copyLabel,
  copiedLabel
}: Readonly<{ label: string; value: string; copyLabel: string; copiedLabel: string }>) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => {
      setCopied(false)
    }, RESET_COPIED_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [copied])

  const handleCopy = useCallback(() => {
    void copyText(value)
    setCopied(true)
  }, [value])

  return (
    <button className="birdie-rc__fair-row" onClick={handleCopy} title={copyLabel} type="button">
      <span className="birdie-rc__fair-label">{label}</span>
      <code className="birdie-rc__fair-value">{displayValue(value)}</code>
      <span className={`birdie-rc__fair-copy${copied ? ' birdie-rc__fair-copy--copied' : ''}`}>
        {copied ? copiedLabel : copyLabel}
      </span>
    </button>
  )
}

/** The shield the fairness block hangs its eyebrow off (the mockup's). */
function ShieldIcon() {
  return (
    <svg aria-hidden="true" className="birdie-rc__shield" viewBox="0 0 24 24">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

/** The disclosure's chevron. */
function ChevronIcon() {
  return (
    <svg aria-hidden="true" className="birdie-rc__fair-more-icon" viewBox="0 0 12 12">
      <path d="M2.5 4.5 6 8l3.5-3.5" />
    </svg>
  )
}

/** …and the "opens in a new tab" mark on the verify pill. */
function ExternalIcon() {
  return (
    <svg aria-hidden="true" className="birdie-rc__verify-icon" viewBox="0 0 24 24">
      <path d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  )
}

export function BirdieFairnessBody({ fairness, verifierHref, t, detailsOpen = false }: Props) {
  const [showDetails, setShowDetails] = useState(detailsOpen)
  // Resolved on EVERY render, deliberately not memoised on the translator. The
  // modal's translator keeps one identity for its whole life and reads whatever
  // language the shared i18n instance is on (the trap `useBirdieTranslate`
  // documents), so a memo keyed on it would keep the previous language after a
  // runtime switch while the sheet's language-pinned one moved on. It is a
  // dozen dictionary lookups; every label is a literal translation call in
  // `birdieCopy.ts`, and those keys already ship in all sixteen locales.
  const translate = t as unknown as TFunction
  const past = birdiePastRoundsCopy(translate)
  const card = birdieRoundCardCopy(translate)
  const copy = {
    provablyFair: past.provablyFair,
    labels: {
      serverSeedHash: past.serverSeedHash,
      revealedSeed: past.revealedSeed,
      chainRoot: past.chainRoot,
      chainIndex: past.chainIndex,
      cardDraw: card.cardDraw,
      paytableHash: past.paytableHash,
      catalogue: past.catalogue,
      clip: past.clip
    },
    copy: past.copy,
    copied: past.copied,
    showMore: past.showMore,
    verify: past.verify,
    verifyHint: card.verifyHint
  }

  const rows = birdieFairnessRows(fairness, copy.labels)
  const { primary, details } = splitFairnessRows(rows)

  return (
    <>
      {/* ── The reveal ─────────────────────────────────────────────────── */}
      {rows.length === 0 ? null : (
        <div className="birdie-rc__fair">
          <div className="birdie-rc__fair-eyebrow">
            <ShieldIcon />
            {copy.provablyFair}
          </div>
          <div className="birdie-rc__fair-rows">
            {primary.map((row) => (
              <FairValue
                copiedLabel={copy.copied}
                copyLabel={copy.copy}
                key={row.key}
                label={row.label}
                value={row.value}
              />
            ))}
          </div>
          {/* One-way disclosure: the platform ships "Show more" in every locale
              and no "show less", and a sheet is transient — once unfolded the
              three rows simply stay until it closes. */}
          {details.length === 0 || showDetails ? null : (
            <button
              aria-expanded={false}
              className="birdie-rc__fair-more"
              onClick={() => {
                setShowDetails(true)
              }}
              type="button"
            >
              {copy.showMore}
              <ChevronIcon />
            </button>
          )}
          {details.length === 0 || !showDetails ? null : (
            <div className="birdie-rc__fair-rows birdie-rc__fair-rows--details">
              {details.map((row) => (
                <FairValue
                  copiedLabel={copy.copied}
                  copyLabel={copy.copy}
                  key={row.key}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* The hand-off to the independent, open-source verifier. A new tab,
          `rel="noopener"` so the opened page gets no handle on this one. */}
      <div className="birdie-rc__actions">
        <a
          className="birdie-rc__verify"
          href={verifierHref}
          rel="noopener noreferrer"
          target="_blank"
        >
          <ExternalIcon />
          {copy.verify}
        </a>
        <span className="birdie-rc__hint">{copy.verifyHint}</span>
      </div>
    </>
  )
}
